// The motion library (WEB_V2_BUILD.md §4): CSS-first, one shared
// IntersectionObserver, nothing hidden until the client says motion is OK.
//
// Classes sections use (the contract):
//   mo-reveal              fade up when it comes into view (data-mo="fade|up|slide|wipe")
//   mo-stagger             its children reveal one after another
//   mo-count               counts its number up from 0 (data-to="1600", optional data-suffix="+")
//   mo-marquee             a moving strip: wrap the items in .mo-marquee__track (duplicated for you)
//
// The server always renders the final state; html.motion-ok is added on the
// client, and only for elements below the fold, so nothing jumps on load.
// Profiles come from the theme (html[data-motion]: none | gentle | lively);
// prefers-reduced-motion shows everything at once and stops marquees.

const SELECTOR = ".mo-reveal, .mo-stagger, .mo-count";
let observer = null;
let mutationObserver = null;
// A "wipe" starts fully clipped (clip-path), and a fully clipped element never counts as in view, so it would stay hidden
// for good (2 Nov: the second hero photo on draksy-for-trades). Its parent is watched instead.
const wipes = new Map();

function reduced() {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function profile() {
    return document.documentElement.getAttribute("data-motion") || "gentle";
}

function inView(el) {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
}

function countUp(el) {
    const to = Number(el.dataset.to);
    if (!Number.isFinite(to) || el.dataset.counted) return;
    el.dataset.counted = "1";
    const suffix = el.dataset.suffix || "";
    const decimals = (String(el.dataset.to).split(".")[1] || "").length;
    const fmt = (n) => n.toLocaleString("en-GB", {minimumFractionDigits: decimals, maximumFractionDigits: decimals}) + suffix;
    if (reduced() || profile() === "none") {
        el.textContent = fmt(to);
        return;
    }
    const start = performance.now();
    const dur = 1200;
    const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        el.textContent = fmt(to * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step);
    };
    el.textContent = fmt(0);
    requestAnimationFrame(step);
}

function reveal(el) {
    // An attribute, not a class: Vue rewrites the whole class list when a :class binding changes (a layout switch
    // in the studio), which dropped "mo-in" and left the element hidden for good.
    el.setAttribute("data-mo-in", "");
    if (el.classList.contains("mo-count")) countUp(el);
    observer?.unobserve(el);
    for (const [parent, els] of wipes) {
        if (!els.includes(el)) continue;
        const left = els.filter((x) => x !== el);
        if (left.length) wipes.set(parent, left);
        else { wipes.delete(parent); observer?.unobserve(parent); }
    }
}

function prepare(el) {
    if (el.dataset.moReady) return;
    el.dataset.moReady = "1";
    if (el.classList.contains("mo-stagger")) {
        Array.from(el.children).forEach((c, i) => c.style.setProperty("--mo-i", String(i)));
    }
    // Already on screen at load: show it as it is (no jump); otherwise wait.
    if (inView(el)) reveal(el);
    else if (el.dataset.mo === "wipe" && el.parentElement) {
        const parent = el.parentElement;
        wipes.set(parent, [...(wipes.get(parent) || []), el]);
        observer.observe(parent);
    } else observer.observe(el);
}

function prepareMarquees(root = document) {
    root.querySelectorAll(".mo-marquee").forEach((m) => {
        if (m.dataset.moReady) return;
        const track = m.querySelector(".mo-marquee__track");
        if (!track) return;
        m.dataset.moReady = "1";
        // Duplicate the items once so the loop is seamless.
        Array.from(track.children).forEach((c) => {
            const copy = c.cloneNode(true);
            copy.setAttribute("aria-hidden", "true");
            track.appendChild(copy);
        });
    });
}

export function installMotionObserver() {
    if (typeof window === "undefined" || observer) return;
    const html = document.documentElement;
    if (reduced() || !("IntersectionObserver" in window)) {
        html.classList.add("motion-reduced");
        document.querySelectorAll(".mo-count").forEach(countUp);
        return;
    }
    observer = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) (wipes.get(e.target) || [e.target]).forEach(reveal);
    }, {rootMargin: "0px 0px -8% 0px", threshold: 0.12});
    document.querySelectorAll(SELECTOR).forEach(prepare);
    prepareMarquees();
    html.classList.add("motion-ok");
    // Sections render after navigation and in the studio preview: pick up new
    // ones as they arrive.
    mutationObserver = new MutationObserver((records) => {
        for (const r of records) {
            for (const n of r.addedNodes) {
                if (!(n instanceof Element)) continue;
                if (n.matches?.(SELECTOR)) prepare(n);
                n.querySelectorAll?.(SELECTOR).forEach(prepare);
                if (n.querySelector?.(".mo-marquee") || n.matches?.(".mo-marquee")) prepareMarquees(n.parentElement || document);
            }
        }
    });
    mutationObserver.observe(document.body, {childList: true, subtree: true});
}

export function uninstallMotionObserver() {
    observer?.disconnect();
    mutationObserver?.disconnect();
    observer = null;
    mutationObserver = null;
    document.documentElement.classList.remove("motion-ok");
}
